import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { BookText } from 'lucide-react';
import MatthewHenryCommentary from './MatthewHenryCommentary';
import { usePremiumGate } from '../hooks/usePremiumGate';
import NowFreeBadge, { markNowFreeSeen } from './NowFreeBadge';

/**
 * Pinned button in the Bible reader chapter title row that opens the
 * public-domain Matthew Henry's Concise Commentary for the current
 * book + chapter inside a slide-in Sheet (right side on desktop, bottom
 * on mobile via `side="right"` defaulting to right which is fine for both).
 */
const MatthewHenryButton = ({ book, chapter, language = 'en' }) => {
  const [open, setOpen] = useState(false);
  const { dialog: premiumDialog } = usePremiumGate();
  const [searchParams] = useSearchParams();
  const titleEs = 'Matthew Henry · Comentario';
  const titleEn = 'Matthew Henry · Commentary';

  // The resourceful Companion's `mh_commentary` action deep-links here
  // with `?mh=1` — auto-open the sheet on first mount when that flag is set.
  useEffect(() => {
    if (searchParams.get('mh') === '1') {
      setOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
    <Sheet open={open} onOpenChange={(v) => { if (v) markNowFreeSeen('mh'); setOpen(v); }}>
      <SheetTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2.5 text-xs gap-1.5 border border-amber-300/50 bg-amber-50/50 hover:bg-amber-100/70 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800/50 dark:hover:bg-amber-900/50"
          data-testid="mh-open-btn"
          title={language === 'es' ? titleEs : titleEn}
        >
          <BookText className="w-3.5 h-3.5" />
          <span className="hidden sm:inline font-medium">
            {language === 'es' ? 'M. Henry' : 'M. Henry'}
          </span>
          <NowFreeBadge seenKey="mh" language={language} />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-hidden flex flex-col p-0"
        data-testid="mh-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-amber-200/60 dark:border-amber-800/40">
          <SheetTitle className="text-amber-900 dark:text-amber-100 flex items-center gap-2">
            <BookText className="w-5 h-5" />
            {language === 'es' ? titleEs : titleEn}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {book} {chapter}
            {' · '}
            {language === 'es'
              ? 'Comentario clásico en dominio público'
              : 'Classic public-domain commentary'}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="flex-1 px-5 py-4">
          {open && (
            <MatthewHenryCommentary
              book={book}
              chapter={chapter}
              language={language}
            />
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
    {premiumDialog}
    </>
  );
};

export default MatthewHenryButton;
